import { ESCRITA_NEGADA_VPS, LEITURA_NEGADA_VPS } from './index';

/**
 * As negações de caminho do OpenCode, escritas na LÍNGUA DE PADRÃO dele.
 *
 * O OpenCode não usa o nosso glob (`glob.ts`). O `Wildcard.match` do binário
 * 1.18.32 (lido no próprio executável, 2026-09-24) faz três coisas que mudam
 * como uma lista se escreve:
 *
 * 1. troca `\` por `/` no texto E no padrão — uma lista só vale nos dois
 *    sistemas;
 * 2. `*` vira `.*` — ATRAVESSA pastas. Não existe "um segmento só";
 * 3. compara sem diferenciar maiúsculas (`/si`), em qualquer sistema.
 *
 * E o texto comparado não é o caminho absoluto: `read` e `edit` pedem com o
 * caminho RELATIVO ao `worktree` da sessão (`path.relative(worktree, arquivo)`),
 * e o `worktree` de pasta sem git é `/`. Por isso uma negação de `/etc/caddy`
 * precisa de várias grafias — ver `padroesOpencodeDoCaminho`.
 *
 * **Estas listas moram na camada gerenciada, que NÃO é piso** (R1 da Fase 0,
 * OC-24): regra de sessão e de agente vem depois e a última que casa vence. Elas
 * valem em dois casos, que são exatamente os que doem: a sessão criada sem
 * regra nenhuma (esquecimento do daemon — OC-9: sem config, executa tudo) e a
 * sessão-FILHA de um subagente, que da mãe só herda `deny` e
 * `external_directory` (função `pi` do binário 1.18.32).
 */

/**
 * Converte um glob do Remoto no curinga do OpenCode: `**` e `*` viram o mesmo
 * `*`, porque lá o `*` já atravessa pastas. A tradução só pode ALARGAR — um
 * `*` que antes parava na barra passa a seguir adiante —, que é o lado certo de
 * errar numa lista de negação.
 */
export function curingaOpencode(glob: string): string {
  return glob.replace(/\\/g, '/').replace(/\*\*/g, '*').replace(/\*+/g, '*');
}

/**
 * O `Wildcard.match` do OpenCode 1.18.32, reescrito fiel ao binário: troca
 * `\` por `/` nos dois lados, escapa os metacaracteres do padrão, `*` vira
 * `.*`, `?` vira `.`, um ` *` no fim vira "espaço e resto, opcional", e compara
 * com as flags `si` — sem diferenciar maiúsculas, em qualquer sistema.
 *
 * Existe para os testes medirem a lista na regra de quem a aplica — e não na
 * nossa, que tem outra semântica para `*`.
 */
export function casaCuringaOpencode(texto: string, padrao: string): boolean {
  const alvo = texto.replace(/\\/g, '/');
  let regex = padrao
    .replace(/\\/g, '/')
    .replace(/[.+^${}()|[\]\\]/g, '\\$&')
    .replace(/\*/g, '.*')
    .replace(/\?/g, '.');
  if (regex.endsWith(' .*')) regex = regex.slice(0, -3) + '( .*)?';
  return new RegExp('^' + regex + '$', 'si').test(alvo);
}

/**
 * As grafias de um caminho ABSOLUTO da VPS que o OpenCode pode comparar.
 *
 * - a absoluta (`/etc/caddy/*`), para quem pedir com ela;
 * - a relativa a `/` (`etc/caddy/*`), que é o `worktree` de pasta sem git;
 * - a relativa a uma pasta qualquer (`*../etc/caddy/*`): o `*` engole os
 *   `../` que sobem da pasta da sessão até a raiz;
 * - para o que mora em `/home/claude`, a relativa a uma pasta VIZINHA
 *   (`*../.claude*`): de `/home/claude/mestre-opencode` até
 *   `/home/claude/.claude` o caminho é `../.claude`, sem `home/claude` no meio.
 *
 * O que fica de fora, dito por extenso:
 * - uma sessão cujo `cwd` é a própria `/home/claude` compara `.claude/x`, sem
 *   `../` — e negar isso negaria a pasta `.claude` de todo projeto. A pasta da
 *   mestre nunca é a `/home/claude`;
 * - uma sessão com `cwd` dentro de `/opt/<x>` compara `/opt/<y>` como `../<y>`,
 *   e negar `*../<y>` negaria também `/home/claude/projetos/<y>` (o `remoto`
 *   mora nos dois). Em `/opt` quem segura é o sistema: o `claude` não escreve
 *   lá.
 */
export function padroesOpencodeDoCaminho(caminhoAbsoluto: string): string[] {
  const curinga = curingaOpencode(caminhoAbsoluto);
  if (!curinga.startsWith('/')) {
    throw new Error(`padroesOpencodeDoCaminho exige caminho absoluto: ${caminhoAbsoluto}`);
  }
  const semRaiz = curinga.slice(1);
  const formas = [curinga, semRaiz, `*../${semRaiz}`];
  const HOME = 'home/claude/';
  if (semRaiz.toLowerCase().startsWith(HOME) && semRaiz.length > HOME.length) {
    formas.push(`*../${semRaiz.slice(HOME.length)}`);
  }
  return [...new Set(formas)];
}

/**
 * Segredos que uma sessão OpenCode não LÊ, em máquina nenhuma — o espelho de
 * `pareceSegredo` (`segredos.ts`) na língua do OpenCode.
 *
 * A régua é a de `pareceSegredo`, e não a do editor (`ARQUIVOS_NEGADOS`), pelo
 * mesmo motivo que ela existe: o que uma sessão OpenCode lê vai para o
 * OpenRouter e para o provedor do modelo (D11), vira linha append-only em
 * `eventos_sessao` e volta pela busca. Ler é mandar para fora.
 *
 * Divergências de `pareceSegredo`, as duas por não haver regex aqui:
 * - `id_[a-z0-9]+(_sk)?` virou a lista dos nomes que o `ssh-keygen` gera — um
 *   `id_*` negaria `src/id_utils.ts` de todo projeto;
 * - `.env` seguido de ponto ou hífen casa em qualquer lugar do CAMINHO, e não só
 *   no nome — uma pasta `x.env.d/` também é negada. Erra acusando.
 */
const PASTAS_SEGREDO_OPENCODE = [
  '.ssh',
  '.aws',
  '.gnupg',
  '.kube',
  '.docker',
  '.git',
  // `.claude`, `.claude-mestre`, `.claude-trabalho`: o `.credentials.json` de
  // cada perfil, e os transcritos de todas as sessões do dono. Custo sabido:
  // as `.claude/rules` de um projeto também caem aqui — quem precisar delas
  // reabre por regra de SESSÃO, depois da lista (a última que casa vence).
  '.claude*',
] as const;

const NOMES_SEGREDO_OPENCODE = [
  'env',
  '.credentials.json',
  '.claude.json',
  'credentials',
  'credentials.json',
  '.git-credentials',
  '.netrc',
  '_netrc',
  '.pgpass',
  '.npmrc',
  '.htpasswd',
  'settings.json',
  '.mcp.json',
  'authorized_keys',
  'known_hosts',
  'id_rsa',
  'id_dsa',
  'id_ecdsa',
  'id_ed25519',
  'id_ecdsa_sk',
  'id_ed25519_sk',
] as const;

const SUFIXOS_SEGREDO_OPENCODE = [
  '.env',
  '.pem',
  '.key',
  '.pfx',
  '.p12',
  '.p8',
  '.jks',
  '.keystore',
  '.kdbx',
  '.ppk',
  '.asc',
  '.gpg',
  '.ovpn',
] as const;

/** `.env.local`, `.env.bak-2026-09-08`, `.env-antigo`: o `.env` com sobrenome. */
const INFIXOS_SEGREDO_OPENCODE = ['.env.', '.env-'] as const;

export const LEITURA_NEGADA_OPENCODE: readonly string[] = [
  ...PASTAS_SEGREDO_OPENCODE.flatMap((p) => [`${p}/*`, `*/${p}/*`]),
  ...NOMES_SEGREDO_OPENCODE.flatMap((n) => [n, `*/${n}`]),
  ...SUFIXOS_SEGREDO_OPENCODE.map((s) => `*${s}`),
  ...INFIXOS_SEGREDO_OPENCODE.map((i) => `*${i}*`),
];

/**
 * A deny-list de caminhos da D3 (mestre OpenCode na VPS), somada às listas que
 * o Claude e o Codex já respeitam lá. A mestre roda com TUDO liberado por regra
 * de sessão (D3); o que está aqui vale na camada gerenciada da VPS como defesa
 * em profundidade — para a sessão-filha, que não herda o `allow` da mãe, e para
 * o esquecimento do daemon.
 */
const CAMINHOS_DA_D3 = [
  '/etc/caddy/**',
  '/var/log/**',
  '/etc/remoto-agent/**',
  '/root/**',
  '/home/claude/.claude*/**',
  '/home/claude/.claude.json',
  '/home/claude/.codex-*/**',
  '/home/claude/.opencode*/**',
] as const;

/*
  Funções, e não constantes: este arquivo e `index.ts` se importam um ao outro,
  e no ESM (vitest) quem é importado roda ANTES — uma constante montada aqui no
  topo leria `LEITURA_NEGADA_VPS` ainda na zona morta. Lida na chamada, a
  ligação já está viva.
*/
export function leituraNegadaOpencodeVps(): string[] {
  return [...new Set([...LEITURA_NEGADA_VPS, ...CAMINHOS_DA_D3])];
}

export function escritaNegadaOpencodeVps(): string[] {
  return [...new Set([...ESCRITA_NEGADA_VPS, ...CAMINHOS_DA_D3])];
}

/**
 * O território da regra 4 do `vps.md`, que é onde a mestre pode sair da própria
 * pasta. Fora dele, `external_directory` é negado. `/tmp` entra porque o
 * OpenCode escreve temporário ali, e o padrão nativo já o liberava.
 */
export const TERRITORIO_OPENCODE_VPS = [
  '/home/claude/*',
  '/opt/remoto/*',
  '/opt/remoto-agent/*',
  '/tmp/*',
] as const;

/**
 * O arquivo da camada gerenciada DENTRO da pasta que o binário lê
 * (`%ProgramData%\opencode` e `/etc/opencode`). O binário também leria um
 * `opencode.jsonc` ao lado — que os instaladores e a sonda tratam como defeito.
 */
export const ARQUIVO_DA_CAMADA_OPENCODE = 'opencode.json';

/**
 * O sha256 (hex, dos BYTES) que a camada gravada tem de ter, por papel da
 * máquina: `infra/opencode/gerenciado.json` nos PCs, `gerenciado-vps.json` na
 * VPS. Os instaladores gravam o arquivo byte a byte e conferem este mesmo
 * hash; a sonda do daemon (`status.ts`, veredito `divergente` sem hash) liga
 * nele pela costura. Um teste recalcula dos arquivos: mexer na camada sem
 * atualizar aqui reprova — e é assim que se sabe que as máquinas precisam do
 * instalador de novo.
 */
export const SHA256_DA_CAMADA_OPENCODE = {
  pc: 'ac36a2eb84867b1f259b7a25ec1261268d581d435ba435ff3c968ca49601fd54',
  vps: 'b72cdb924a52b0b90853c81e6d85b68260ad7971aff74395e115ebfc0a536f1d',
} as const satisfies Record<'pc' | 'vps', string>;

/** O hash esperado da camada para o papel da máquina (`PAPEL` do daemon). */
export function hashDaCamadaOpencode(papel: 'pc' | 'vps'): string {
  return SHA256_DA_CAMADA_OPENCODE[papel];
}

type Acao = 'allow' | 'ask' | 'deny';
type RegrasPorPadrao = Record<string, Acao>;

/** O objeto `permission` da camada gerenciada, na ordem em que ele é avaliado. */
export interface PermissaoGerenciadaOpencode {
  '*': Acao;
  read: RegrasPorPadrao;
  edit?: RegrasPorPadrao;
  external_directory?: RegrasPorPadrao;
}

function negar(padroes: readonly string[]): RegrasPorPadrao {
  return Object.fromEntries(padroes.map((p) => [p, 'deny' as const]));
}

/**
 * A `permission` da camada gerenciada, montada daqui e conferida contra os
 * arquivos de `infra/opencode/` (o JSON é espelho; se divergir, o teste morde).
 *
 * `"*": "ask"` vem PRIMEIRO: é o que a sessão sem regra e a sessão-filha
 * encontram quando nada mais casa. Sem ele, o padrão nativo é `"*": "allow"`.
 * Efeito colateral sabido: o `edit: deny` nativo do agente `plan` vem ANTES da
 * config e perde para esse `ask` — as regras de sessão do modo `plan` têm de
 * negar edição por conta própria (d2b).
 */
export function permissaoGerenciadaOpencode(papel: 'pc' | 'vps'): PermissaoGerenciadaOpencode {
  if (papel === 'pc') {
    return { '*': 'ask', read: negar(LEITURA_NEGADA_OPENCODE) };
  }
  const caminhos = (lista: readonly string[]) => lista.flatMap(padroesOpencodeDoCaminho);
  return {
    '*': 'ask',
    read: negar([
      ...new Set([...LEITURA_NEGADA_OPENCODE, ...caminhos(leituraNegadaOpencodeVps())]),
    ]),
    edit: negar([...new Set(caminhos(escritaNegadaOpencodeVps()))]),
    external_directory: {
      '*': 'deny',
      ...Object.fromEntries(TERRITORIO_OPENCODE_VPS.map((p) => [p, 'allow' as const])),
    },
  };
}
